import React, { useState } from 'react';
import { styled } from '@mui/material';

import { ActivityItem } from 'components/organisms/Dashboard';
import { useViewport } from 'contexts/viewport';
import { Accordion, AccordionSummary, AccordionDetails, Button, Stack, Tabs, Text } from 'shell/components';
import { TYPES, ACTIVITY_ACTIONS } from 'utils/constants';
import { getTaskStatus, getUserPreferredFullName } from 'utils/helper';

const ActivityList = ({
    currentAccountId,
    activityList = [],
    groupMembers,
    instructorMembers,
    setActivitySettings,
}) => {
    const { device } = useViewport();

    const [route, setRoute] = useState('today');

    const incrementMaxActivityNum = () => {
        setActivitySettings((prev) => ({ ...prev, max: prev.max + 10 }));
    };

    const handleTabSwitch = (route) => {
        setActivitySettings((prev) => {
            if (prev.mode === route) return prev;
            return {
                mode: route,
                max: 10,
            };
        });
        setRoute(route);
    };

    const renderActivityList = () => {
        const getNameOrPronoun = (owner_account_id) => {
            if (owner_account_id === currentAccountId) return 'You';
            return getUserPreferredFullName(
                [...groupMembers, ...instructorMembers].find((member) => member.owner_account_id === owner_account_id)
            );
        };

        if (!activityList.total) {
            return (
                <Stack spacing={5}>
                    <Text sx={{ mt: 5 }} align={'center'} color={'light'} variant={'p2'}>
                        No activities found.
                    </Text>
                </Stack>
            );
        }

        return (
            <Stack sx={{ mt: 3 }} spacing={5}>
                {activityList.results
                    .filter(({ action, type }) => {
                        if (type === TYPES.TASK) {
                            return [
                                ACTIVITY_ACTIONS.CREATE,
                                ACTIVITY_ACTIONS.UPDATE,
                                ACTIVITY_ACTIONS.DELETE,
                                ACTIVITY_ACTIONS.STATUS,
                            ].includes(action);
                        } else if (type === TYPES.FEEDBACK) {
                            return [ACTIVITY_ACTIONS.CREATE].includes(action);
                        } else if (type === TYPES.FILE) {
                            return [ACTIVITY_ACTIONS.CREATE].includes(action);
                        } else if (type === TYPES.SHARED_URL) {
                            return [ACTIVITY_ACTIONS.CREATE].includes(action);
                        }
                        return false;
                    })
                    .map(({ id, action, type, created_on, owner_account_id, data = {} }) => {
                        const owner = [...groupMembers, ...instructorMembers].find(
                            (item) => item.owner_account_id === owner_account_id
                        );
                        if (!owner || !data) return;
                        let text = '';

                        if (type === TYPES.TASK) {
                            if (action === ACTIVITY_ACTIONS.CREATE) {
                                text = `${getNameOrPronoun(owner_account_id)} created the task "${data.title}" in "${
                                    getTaskStatus(data.status).title
                                }".`;
                            } else if (action === ACTIVITY_ACTIONS.UPDATE) {
                                text = `${getNameOrPronoun(owner_account_id)} updated the task "${data.title}".`;
                            } else if (action === ACTIVITY_ACTIONS.DELETE) {
                                text = `${getNameOrPronoun(owner_account_id)} deleted "${data.title}".`;
                            } else if (action === ACTIVITY_ACTIONS.STATUS) {
                                text = `${getNameOrPronoun(owner_account_id)} updated the status of "${
                                    data.title
                                }" to "${getTaskStatus(data.status).title}".`;
                            }
                        } else if (type === TYPES.FEEDBACK) {
                            if (action === ACTIVITY_ACTIONS.CREATE) {
                                text = `${getNameOrPronoun(owner_account_id)} left a comment on "${data.title}".`;
                            }
                        } else if (type === TYPES.FILE) {
                            if (action === ACTIVITY_ACTIONS.CREATE) {
                                text = `${getNameOrPronoun(owner_account_id)} uploaded a file "${data.data.name}".`;
                            }
                        } else if (type === TYPES.SHARED_URL) {
                            if (action === ACTIVITY_ACTIONS.CREATE) {
                                text = `${getNameOrPronoun(owner_account_id)} saved a link to "${data.name}".`;
                            }
                        }

                        return <ActivityItem key={id} user={owner} text={text} createdOn={created_on} />;
                    })}
            </Stack>
        );
    };

    const tabList = [
        {
            key: 'today',
            value: 'Today',
            content: (
                <>
                    {renderActivityList()}
                    {activityList.results.length < activityList.total && (
                        <Button variant={'tertiary'} onClick={incrementMaxActivityNum}>
                            Load More
                        </Button>
                    )}
                </>
            ),
        },
        {
            key: 'pastweek',
            value: 'Past Week',
            content: (
                <>
                    {renderActivityList()}
                    {activityList.results.length < activityList.total && (
                        <Button variant={'tertiary'} onClick={incrementMaxActivityNum}>
                            Load More
                        </Button>
                    )}
                </>
            ),
        },
    ];

    const ActivityHeader = (
        <Text variant={'sh2'} weight={'semibold'}>
            Activity
        </Text>
    );

    const ActivityBody = (
        <CustomTabs aria-label={'select activity tab'} options={tabList} selected={route} onChange={handleTabSwitch} />
    );

    return (
        <CustomStack device={device} spacing={2}>
            {device === 'mobile' ? (
                <Accordion>
                    <AccordionSummary id={'activity-header'} aria-controls={'activity-content'}>
                        {ActivityHeader}
                    </AccordionSummary>
                    <AccordionDetails>{ActivityBody}</AccordionDetails>
                </Accordion>
            ) : (
                <>
                    {ActivityHeader}
                    {ActivityBody}
                </>
            )}
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ device, theme }) => {
    return {
        minWidth: 0,
        padding: theme.spacing(5, 5, 0),
        background: theme.palette.ensightful.shades[0],
        ...(device === 'mobile' && {
            padding: theme.spacing(5),
        }),
    };
});

const CustomTabs = styled(Tabs)(({ theme }) => {
    return {
        height: 'calc(100% - 24px - 8px)',
        '& > .MuiBox-root': {
            height: 'calc(100% - 34px)',
            paddingBottom: theme.spacing(4),
            overflowY: 'auto',
        },
    };
});

export default ActivityList;
